// Describes how the total discount to NAV moved after an FX change. A positive value is a discount,
// a negative value a premium; "increased"/"decreased" is judged by distance from zero (magnitude),
// so a premium shrinking from 1.00% to 0.50% reads as a premium *decrease* (closer to zero).
export const describeDiscountToNavChange = (beforePct: number, afterPct: number): string => {
    // Round to the 2 d.p. we display first, then derive direction/delta from the rounded values,
    // so the word ("increased"/"decreased"/"unchanged"), the endpoints, and the delta can never disagree.
    const round2 = (v: number): number => Math.round(v * 100) / 100;
    const b = round2(beforePct);
    const a = round2(afterPct);

    const label = (v: number): string => (v < 0 ? "premium" : "discount");
    const magnitude = (v: number): string => `${Math.abs(v).toFixed(2)}%`;

    // Sign flip between discount and premium — describe the crossover explicitly.
    if (b !== 0 && a !== 0 && Math.sign(b) !== Math.sign(a)) {
        return `${magnitude(b)} ${label(b)} to NAV flipped to a ${magnitude(a)} ${label(a)}`;
    }

    const kind = label(a !== 0 ? a : b);
    const magDelta = round2(Math.abs(a) - Math.abs(b));
    if (magDelta === 0) {
        return `${kind} to NAV unchanged at ${magnitude(a)}`;
    }
    const direction = magDelta > 0 ? "increased" : "decreased";
    const sign = magDelta > 0 ? "+" : "-";
    return `${kind} to NAV ${direction} ${magnitude(b)} → ${magnitude(a)} (${sign}${Math.abs(magDelta).toFixed(2)}%)`;
};